import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import ActivityPanel from '../components/ActivityPanel';

export default function ContactDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);

  const load = () => {
    setLoading(true);
    api.get(`/contacts/${id}`)
      .then(r => {
        setContact(r.data.contact);
        setForm(r.data.contact);
      })
      .catch(() => navigate('/contacts'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get('/accounts?limit=100').then(r => setAccounts(r.data.accounts || [])).catch(() => {});
  }, [id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/contacts/${id}`, {
        ...form,
        company_id: form.company_id || null,
      });
      setContact(r.data.contact);
      setEditing(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this contact? This cannot be undone.')) return;
    await api.delete(`/contacts/${id}`);
    navigate('/contacts');
  };

  const statusBadge = s => {
    const map = { active:'badge-green', lead:'badge-amber', customer:'badge-blue', cold:'badge-gray', inactive:'badge-gray' };
    return <span className={`badge ${map[s]||'badge-gray'}`}>{s}</span>;
  };

  if (loading) return <div style={{ fontSize:13, color:'var(--text-tertiary)' }}>Loading…</div>;
  if (!contact) return null;

  return (
    <>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
        <button className="btn" onClick={() => navigate('/contacts')}>← Back</button>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:20, fontWeight:600 }}>{contact.first_name} {contact.last_name}</div>
          <div style={{ fontSize:12, color:'var(--text-secondary)', marginTop:2, display:'flex', gap:8, alignItems:'center' }}>
            {contact.title && <span>{contact.title}</span>}
            {contact.company_name && <span>· {contact.company_name}</span>}
            {statusBadge(contact.status)}
          </div>
        </div>
        <button className="btn" onClick={() => setEditing(!editing)}>{editing ? 'Cancel' : 'Edit'}</button>
        <button className="btn" onClick={handleDelete} style={{ color:'var(--danger)' }}>Delete</button>
      </div>

      {/* Edit Form */}
      {editing && (
        <div className="card" style={{ marginBottom:16 }}>
          <div className="section-title" style={{ marginBottom:14 }}>Edit contact</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <label className="form-label">First name *</label>
              <input className="form-input" value={form.first_name||''}
                onChange={e => setForm({...form, first_name:e.target.value})} />
            </div>
            <div>
              <label className="form-label">Last name *</label>
              <input className="form-input" value={form.last_name||''}
                onChange={e => setForm({...form, last_name:e.target.value})} />
            </div>
            <div>
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={form.email||''}
                onChange={e => setForm({...form, email:e.target.value})} />
            </div>
            <div>
              <label className="form-label">Phone</label>
              <input className="form-input" value={form.phone||''}
                onChange={e => setForm({...form, phone:e.target.value})} />
            </div>
            <div>
              <label className="form-label">Title</label>
              <input className="form-input" value={form.title||''}
                onChange={e => setForm({...form, title:e.target.value})} />
            </div>
            <div>
              <label className="form-label">Account</label>
              <select className="form-input" value={form.company_id||''}
                onChange={e => setForm({...form, company_id:e.target.value})}>
                <option value="">— No account —</option>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Status</label>
              <select className="form-input" value={form.status||'lead'}
                onChange={e => setForm({...form, status:e.target.value})}>
                <option value="lead">Lead</option>
                <option value="active">Active</option>
                <option value="customer">Customer</option>
                <option value="cold">Cold</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label className="form-label">Notes</label>
              <input className="form-input" value={form.notes||''}
                onChange={e => setForm({...form, notes:e.target.value})}
                placeholder="Optional notes" />
            </div>
          </div>
          <div style={{ display:'flex', gap:8, marginTop:14 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button className="btn" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Main layout — left + right activity panel */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 340px', gap:16, alignItems:'start' }}>

        {/* LEFT SIDE */}
        <div className="card">
          <div className="section-title">Contact details</div>
          <table className="data-table">
            <tbody>
              <tr><td style={{ color:'var(--text-secondary)', width:120 }}>Email</td>
                <td>{contact.email
                  ? <a href={`mailto:${contact.email}`} style={{ color:'var(--accent-dark)' }}>{contact.email}</a>
                  : '—'}
                </td>
              </tr>
              <tr><td style={{ color:'var(--text-secondary)' }}>Phone</td>
                <td>{contact.phone
                  ? <a href={`tel:${contact.phone}`} style={{ color:'var(--accent-dark)' }}>{contact.phone}</a>
                  : '—'}
                </td>
              </tr>
              <tr><td style={{ color:'var(--text-secondary)' }}>Title</td><td>{contact.title || '—'}</td></tr>
              <tr><td style={{ color:'var(--text-secondary)' }}>Account</td>
                <td>{contact.company_name
                  ? <span style={{ color:'var(--accent-dark)', cursor:'pointer' }}
                      onClick={() => navigate(`/accounts/${contact.company_id}`)}>
                      {contact.company_name}
                    </span>
                  : '—'}
                </td>
              </tr>
              <tr><td style={{ color:'var(--text-secondary)' }}>Status</td><td>{statusBadge(contact.status)}</td></tr>
              <tr><td style={{ color:'var(--text-secondary)' }}>Source</td><td><span className="badge badge-gray">{contact.source}</span></td></tr>
              {contact.notes && <tr><td style={{ color:'var(--text-secondary)' }}>Notes</td><td>{contact.notes}</td></tr>}
              <tr><td style={{ color:'var(--text-secondary)' }}>Created</td><td style={{ fontSize:12 }}>{new Date(contact.created_at).toLocaleDateString()}</td></tr>
            </tbody>
          </table>
        </div>

        {/* RIGHT SIDE — Activity Panel */}
        <div className="card" style={{ position:'sticky', top:20 }}>
          <div className="section-title" style={{ marginBottom:12 }}>Activity</div>
          <ActivityPanel contactId={id} />
        </div>

      </div>
    </>
  );
}